import { ActionButton } from "@via/components/action-button/action-button.tsx";
import Drawer from "@via/components/drawer.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { BUTTONS, ROUTES } from "@via/utils/constants.tsx";
import { type JSX, useCallback, useEffect, useState } from "react";
import { useGetOrganizationRunEnvironments } from "@via/pages/organization/run-environments/organization-run-environments.api.tsx";
import OrganizationRunEnvironmentsTable from "@via/pages/organization/run-environments/run-environments-table.tsx";
import { useNavigate, useParams } from "react-router-dom";
import OrganizationRunEnvironmentForm from "@via/pages/organization/run-environments/run-environments-form.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { type ProfileClusterForClient } from "common";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext.tsx";
import { canManageOrganization } from "@via/hooks/organization-access.ts";

export default function OrganizationRunEnvironments(): JSX.Element {
  const { organizationId = "" } = useParams();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [runEnvironmentFormKey, setRunEnvironmentFormKey] = useState(1);
  const [orderedRunEnvironments, setOrderedRunEnvironments] = useState<
    ProfileClusterForClient[]
  >([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // undefined = the user has not loaded yet; only a definite "no" redirects.
    if (canManageOrganization(user, organizationId) === false) {
      navigate(`/organization/${organizationId}/activity`);
      return;
    }
  }, [user, organizationId]);

  useEffect(() => {
    setRunEnvironmentFormKey(prev => prev + 1);
  }, [isDrawerOpen]);

  const {
    data: runEnvironments,
    isLoading: isRunEnvironmentsLoading,
    isSuccess: isRunEnvironmentsSuccess,
  } = useGetOrganizationRunEnvironments({ id: organizationId });

  useEffect(() => {
    if (isRunEnvironmentsSuccess && runEnvironments) {
      // Sort run environments by isSystemGenerated
      const sortedEnvironments = [...runEnvironments].sort((a, b) => {
        if (a.isSystemGenerated && !b.isSystemGenerated) return -1;
        if (!a.isSystemGenerated && b.isSystemGenerated) return 1;
        return 0; // Keep original order if both are the same
      });
      setOrderedRunEnvironments(sortedEnvironments);
    }
  }, [runEnvironments, isRunEnvironmentsSuccess]);

  const getRunEnvironmentsContext = useCallback(() => {
    const systemGeneratedCount = orderedRunEnvironments.filter(
      env => env.isSystemGenerated
    ).length;
    const customCount = orderedRunEnvironments.filter(
      env => !env.isSystemGenerated
    ).length;

    return {
      organizationId,
      summary: {
        totalEnvironments: orderedRunEnvironments.length,
        systemGeneratedCount,
        customCount,
      },
      runEnvironments: orderedRunEnvironments.map(env => ({
        id: env.id,
        name: env.name,
        containerType: env.containerType,
        executor: env.executor,
        executorJob: env.executorJob,
        nextPath: env.nextPath,
        username: env.username,
        hostname: env.hostname,
        isSystemGenerated: env.isSystemGenerated,
        proxy: env.proxy,
      })),
    };
  }, [organizationId, orderedRunEnvironments]);

  useRegisterCortexContext({
    routePattern: ROUTES.ORGANIZATION_RUN_ENVIRONMENTS,
    contextName: "Organization Run Environments",
    getContext: getRunEnvironmentsContext,
    systemPrompt:
      "You are analyzing organization run environments page. Help answer questions about run environments, container types, executors, connection details, and environment configuration.",
    enabled: !!organizationId && isRunEnvironmentsSuccess,
  });

  return (
    <div>
      <div className="p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-full sm:rounded-lg">
        <div className="via-page-heading-wrapper">
          <h2 className="via-h2">Run Environments</h2>

          <ActionButton
            button={BUTTONS.CREATE_ENVIRONMENT}
            onClick={() => {
              setDrawerOpen(true);
            }}
          />
        </div>

        {isRunEnvironmentsLoading ? (
          <div className="my-4">
            <SkeletonLoader count={12} />
          </div>
        ) : (
          <OrganizationRunEnvironmentsTable
            organizationId={organizationId}
            runEnvironments={orderedRunEnvironments || []}
          />
        )}

        <Drawer
          id="run-environments"
          open={isDrawerOpen}
          setOpen={setDrawerOpen}>
          <OrganizationRunEnvironmentForm
            organizationId={organizationId}
            setOpen={setDrawerOpen}
            key={runEnvironmentFormKey}
          />
        </Drawer>
      </div>
    </div>
  );
}
